import React, { useEffect, useMemo, useState } from "react";
import { Bell, CalendarDays, CheckCircle2, KeyRound, Library, MapPinned, MessageSquare, PencilLine, ShoppingBag, Trash2, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { EmptyState, PanelActionButton, SectionPanel, StatusPill, SummaryTile } from "@/components/platform/ContentPrimitives";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/formatters";

function inputClass() {
  return "h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35";
}

function statusLabel(item) {
  const rawStatus = item?.revision_status || item?.estado || item?.status || "";
  const normalized = String(rawStatus).toLowerCase();
  if (normalized.includes("correccion") || normalized.includes("corrección") || normalized.includes("needs_changes")) {
    return "Requiere correcciones";
  }
  if (normalized.includes("pendiente")) {
    return "Publicación en proceso";
  }
  if (normalized.includes("publicado") || normalized.includes("approved")) {
    return "Aprobada";
  }
  if (normalized.includes("rechaz") || normalized.includes("rejected")) {
    return "Rechazada";
  }
  return rawStatus || "Sin estado";
}

function statusTone(item) {
  const label = statusLabel(item).toLowerCase();
  if (label.includes("aprobada")) {
    return "positive";
  }
  if (label.includes("rechazada")) {
    return "danger";
  }
  if (label.includes("correcciones")) {
    return "warning";
  }
  return "warning";
}

function notificationEditPath(notification) {
  const id = notification?.entidad_id;
  const paths = {
    initiative: `/registrar-iniciativa?edit=${id}`,
    marketplace_post: `/crear-publicacion?edit=${id}`,
    event: `/calendario?edit=${id}`,
    forum_question: `/conocimiento?tab=foro&editQuestion=${id}`,
    library_document: `/conocimiento?tab=biblioteca&editDocument=${id}`,
  };
  return id ? paths[notification?.entidad_tipo] : null;
}

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await api.get("/profile/overview");
      setOverview(response.data);
    } catch (error) {
      setPasswordMessage({ type: "error", text: "No fue posible cargar el perfil." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOverview();
    }
  }, [user]);

  const summary = useMemo(() => overview?.summary || {}, [overview]);

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordMessage({ type: "", text: "" });

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessage({ type: "error", text: "Las contrasenas no coinciden." });
      return;
    }

    setSavingPassword(true);
    try {
      await api.put("/auth/password", passwordForm);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setPasswordMessage({ type: "success", text: "Contrasena actualizada. Inicie sesion nuevamente cuando lo necesite." });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setPasswordMessage({ type: "error", text: typeof detail === "string" ? detail : "No fue posible cambiar la contrasena." });
    } finally {
      setSavingPassword(false);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await api.post("/profile/notifications/read-all");
      await fetchOverview();
    } catch (error) {
      setPasswordMessage({ type: "error", text: "No fue posible actualizar las notificaciones." });
    }
  };

  const handleDeleteInitiative = async (initiative) => {
    const confirmed = window.confirm(
      `¿Desea eliminar la iniciativa "${initiative.nombre}"? Esta acción no se puede deshacer.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/initiatives/${initiative.id}`);
      await fetchOverview();
    } catch (error) {
      setPasswordMessage({ type: "error", text: "No fue posible eliminar la iniciativa." });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="platform-hero-banner rounded-[34px] p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">Mi cuenta</p>
        <h1 className="mt-3 flex items-center gap-3 font-heading text-4xl font-semibold text-primary sm:text-5xl">
          <UserRound className="h-10 w-10" />
          Perfil y publicaciones
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-mutedForeground">
          Consulte el estado de su cuenta, revise sus publicaciones por modulo y navegue directo a los formularios de
          edición cuando necesite actualizar su información.
        </p>

        <div className="mt-6 grid gap-4 md:grid-cols-5">
          <SummaryTile icon={MapPinned} label="Mapa" value={summary.initiatives || 0} description="Iniciativas propias registradas." />
          <SummaryTile icon={ShoppingBag} label="Directorio" value={summary.marketplace_posts || 0} description="Publicaciones propias dentro del directorio." />
          <SummaryTile icon={CalendarDays} label="Calendario" value={summary.events || 0} description="Eventos propios registrados." />
          <SummaryTile icon={MessageSquare} label="Foro" value={summary.forum_questions || 0} description="Preguntas creadas dentro del foro." />
          <SummaryTile icon={Library} label="Biblioteca" value={summary.library_documents || 0} description="Documentos cargados en biblioteca." />
        </div>
      </div>

      {loading ? (
        <div className="platform-elevated-surface mt-6 rounded-[28px] border px-6 py-12 text-center text-mutedForeground">
          Cargando perfil...
        </div>
      ) : null}

      {!loading ? (
        <div className="mt-6 grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-6">
            <SectionPanel title="Datos personales" eyebrow="Cuenta" description="Informacion principal del usuario autenticado.">
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  ["Nombres", overview?.user?.nombres],
                  ["Apellidos", overview?.user?.apellidos],
                  ["Correo", overview?.user?.email],
                  ["Celular", overview?.user?.celular],
                  ["Municipio", overview?.user?.municipio],
                  ["Comuna", overview?.user?.comuna],
                  ["Barrio", overview?.user?.barrio],
                ].map(([label, value]) => (
                  <div key={label} className="platform-soft-panel rounded-[22px] border border-primary/10 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/60">{label}</p>
                    <p className="mt-2 text-sm text-textMain">{value || "Sin dato"}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <StatusPill tone={overview?.user?.status === "active" ? "positive" : "warning"}>{overview?.user?.status}</StatusPill>
                <StatusPill tone="neutral">{overview?.user?.rol || overview?.user?.role}</StatusPill>
                <StatusPill tone="neutral">
                  {overview?.user?.comparte_contacto ? "Contacto publico autorizado" : "Contacto privado"}
                </StatusPill>
              </div>
            </SectionPanel>

            <SectionPanel title="Cambiar contrasena" eyebrow="Seguridad" description="Actualice la contrasena de acceso cuando sea necesario.">
              <form className="space-y-4" onSubmit={handlePasswordChange}>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-primary">Contrasena actual</span>
                  <input type="password" value={passwordForm.current_password} onChange={(event) => setPasswordForm((current) => ({ ...current, current_password: event.target.value }))} className={inputClass()} required />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-primary">Nueva contrasena</span>
                  <input type="password" value={passwordForm.new_password} onChange={(event) => setPasswordForm((current) => ({ ...current, new_password: event.target.value }))} className={inputClass()} required />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-primary">Confirmar nueva contrasena</span>
                  <input type="password" value={passwordForm.confirm_password} onChange={(event) => setPasswordForm((current) => ({ ...current, confirm_password: event.target.value }))} className={inputClass()} required />
                </label>

                {passwordMessage.text ? (
                  <div className={`rounded-[20px] px-4 py-3 text-sm ${passwordMessage.type === "success" ? "bg-[#EFF8EF] text-[#2F6B3A]" : "bg-[#FFF1EC] text-[#8A3D22]"}`}>
                    {passwordMessage.text}
                  </div>
                ) : null}

                <PanelActionButton type="submit" disabled={savingPassword}>
                  <KeyRound className="h-4 w-4" />
                  {savingPassword ? "Actualizando..." : "Actualizar contrasena"}
                </PanelActionButton>
              </form>
            </SectionPanel>

            <SectionPanel
              title="Notificaciones"
              eyebrow="Bandeja"
              description="Cambios de estado y eventos recientes sobre sus contenidos."
              actions={
                <PanelActionButton tone="secondary" onClick={markAllNotificationsAsRead}>
                  <CheckCircle2 className="h-4 w-4" />
                  Marcar todas como leidas
                </PanelActionButton>
              }
            >
              {!overview?.notifications?.length ? (
                <EmptyState icon={Bell} title="Sin notificaciones recientes" description="Aqui apareceran cambios de estado y avisos administrativos." />
              ) : (
                <div className="space-y-3">
                  {overview.notifications.map((notification) => {
                    const editPath = notificationEditPath(notification);
                    const requiresChanges = notification.mensaje?.toLowerCase().includes("correcciones");
                    return (
                    <article key={notification.id} className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusPill tone={notification.leida ? "neutral" : "warning"}>{notification.leida ? "Leida" : "Nueva"}</StatusPill>
                        <StatusPill tone="neutral">{notification.tipo}</StatusPill>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-textMain">{notification.mensaje}</p>
                      <p className="mt-2 text-xs text-mutedForeground">{formatDateTime(notification.created_at)}</p>
                      {requiresChanges && editPath ? (
                        <div className="mt-3">
                          <PanelActionButton tone="secondary" onClick={() => navigate(editPath)}>
                            <PencilLine className="h-4 w-4" />
                            Corregir ahora
                          </PanelActionButton>
                        </div>
                      ) : null}
                    </article>
                    );
                  })}
                </div>
              )}
            </SectionPanel>
          </div>

          <div className="space-y-6">
            <SectionPanel title="Mis publicaciones" eyebrow="Gestion por modulo" description="Use estos accesos para editar o crear nuevamente contenido dentro de cada modulo.">
              <div className="grid gap-4 md:grid-cols-2">
                <article className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <h3 className="font-heading text-xl font-semibold text-primary">Mapa territorial</h3>
                  <p className="mt-2 text-sm leading-6 text-mutedForeground">Administre las huertas, pacas o iniciativas que registró en el mapa.</p>
                  <div className="mt-4 space-y-3">
                    {(overview?.content?.initiatives || []).slice(0, 4).map((initiative) => (
                      <div key={initiative.id} className="rounded-[18px] border border-primary/10 bg-white p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-primary">{initiative.nombre}</p>
                            <p className="mt-1 text-xs text-mutedForeground">
                              {[initiative.municipio, initiative.comuna, initiative.barrio].filter(Boolean).join(" / ")}
                            </p>
                            <div className="mt-2">
                              <StatusPill tone={statusTone(initiative)}>{statusLabel(initiative)}</StatusPill>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <PanelActionButton tone="secondary" onClick={() => navigate(`/registrar-iniciativa?edit=${initiative.id}`)}>
                              <PencilLine className="h-4 w-4" />
                              Editar
                            </PanelActionButton>
                            <PanelActionButton tone="danger" onClick={() => handleDeleteInitiative(initiative)}>
                              <Trash2 className="h-4 w-4" />
                              Eliminar
                            </PanelActionButton>
                          </div>
                        </div>
                      </div>
                    ))}
                    {!overview?.content?.initiatives?.length ? (
                      <p className="text-sm text-mutedForeground">Aún no tiene iniciativas registradas.</p>
                    ) : null}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PanelActionButton onClick={() => navigate("/mapa")}>Ir al mapa</PanelActionButton>
                    <PanelActionButton tone="secondary" onClick={() => navigate("/registrar-iniciativa")}>Nueva iniciativa</PanelActionButton>
                  </div>
                </article>

                <article className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <h3 className="font-heading text-xl font-semibold text-primary">Directorio</h3>
                  <p className="mt-2 text-sm leading-6 text-mutedForeground">Administre publicaciones del directorio comunitario.</p>
                  <div className="mt-4 space-y-3">
                    {(overview?.content?.marketplace_posts || []).slice(0, 3).map((post) => (
                      <div key={post.id} className="rounded-[18px] border border-primary/10 bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-primary">{post.titulo}</p>
                            <StatusPill tone={statusTone(post)}>{statusLabel(post)}</StatusPill>
                          </div>
                          <PanelActionButton tone="secondary" onClick={() => navigate(`/crear-publicacion?edit=${post.id}`)}>
                            <PencilLine className="h-4 w-4" />
                            Editar
                          </PanelActionButton>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PanelActionButton onClick={() => navigate("/directorio")}>Ir al directorio</PanelActionButton>
                    <PanelActionButton tone="secondary" onClick={() => navigate("/crear-publicacion")}>Nueva publicacion</PanelActionButton>
                  </div>
                </article>

                <article className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <h3 className="font-heading text-xl font-semibold text-primary">Calendario</h3>
                  <p className="mt-2 text-sm leading-6 text-mutedForeground">Administre encuentros, reuniones y actividades.</p>
                  <div className="mt-4 space-y-3">
                    {(overview?.content?.events || []).slice(0, 3).map((item) => (
                      <div key={item.id} className="rounded-[18px] border border-primary/10 bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-primary">{item.nombre}</p>
                            <StatusPill tone={statusTone(item)}>{statusLabel(item)}</StatusPill>
                          </div>
                          <PanelActionButton tone="secondary" onClick={() => navigate(`/calendario?edit=${item.id}`)}>
                            <PencilLine className="h-4 w-4" />
                            Editar
                          </PanelActionButton>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PanelActionButton onClick={() => navigate("/calendario")}>Ir al calendario</PanelActionButton>
                  </div>
                </article>

                <article className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <h3 className="font-heading text-xl font-semibold text-primary">Foro</h3>
                  <p className="mt-2 text-sm leading-6 text-mutedForeground">Revise sus preguntas y respuestas publicadas.</p>
                  <div className="mt-4 space-y-3">
                    {(overview?.content?.forum_questions || []).slice(0, 3).map((item) => (
                      <div key={item.id} className="rounded-[18px] border border-primary/10 bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-primary">{item.titulo}</p>
                            <StatusPill tone={statusTone(item)}>{statusLabel(item)}</StatusPill>
                          </div>
                          <PanelActionButton tone="secondary" onClick={() => navigate(`/conocimiento?tab=foro&editQuestion=${item.id}`)}>
                            <PencilLine className="h-4 w-4" />
                            Editar
                          </PanelActionButton>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PanelActionButton onClick={() => navigate("/conocimiento?tab=foro")}>Ir al foro</PanelActionButton>
                  </div>
                </article>

                <article className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <h3 className="font-heading text-xl font-semibold text-primary">Biblioteca</h3>
                  <p className="mt-2 text-sm leading-6 text-mutedForeground">Gestione sus recursos y documentos cargados.</p>
                  <div className="mt-4 space-y-3">
                    {(overview?.content?.library_documents || []).slice(0, 3).map((item) => (
                      <div key={item.id} className="rounded-[18px] border border-primary/10 bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-primary">{item.titulo}</p>
                            <StatusPill tone={statusTone(item)}>{statusLabel(item)}</StatusPill>
                          </div>
                          <PanelActionButton tone="secondary" onClick={() => navigate(`/conocimiento?tab=biblioteca&editDocument=${item.id}`)}>
                            <PencilLine className="h-4 w-4" />
                            Editar
                          </PanelActionButton>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PanelActionButton onClick={() => navigate("/conocimiento?tab=biblioteca")}>Ir a biblioteca</PanelActionButton>
                  </div>
                </article>
              </div>
            </SectionPanel>
          </div>
        </div>
      ) : null}
    </div>
  );
}
